import React, { forwardRef } from 'react';
import './Input.css';

/**
 * 统一输入框组件
 * Unified Input Component
 * 
 * @param {Object} props
 * @param {'text'|'email'|'password'|'number'|'datetime-local'|'date'|'time'} props.type - 输入类型
 * @param {'sm'|'md'|'lg'} props.size - 输入框尺寸
 * @param {boolean} props.disabled - 是否禁用
 * @param {boolean} props.error - 是否有错误
 * @param {string} props.placeholder - 占位符
 * @param {string} props.label - 标签文本
 * @param {string} props.helperText - 帮助文本
 * @param {string} props.errorText - 错误文本
 * @param {React.ReactNode} props.prefix - 前缀内容
 * @param {React.ReactNode} props.suffix - 后缀内容
 * @param {string} props.className - 额外的CSS类名
 */
const Input = forwardRef(({
  type = 'text',
  size = 'md',
  disabled = false,
  error = false,
  placeholder,
  label,
  helperText,
  errorText,
  prefix,
  suffix,
  className = '',
  ...props
}, ref) => {
  const baseClass = 'input';
  const sizeClass = `input--${size}`;
  const errorClass = error ? 'input--error' : '';
  const disabledClass = disabled ? 'input--disabled' : '';
  const prefixClass = prefix ? 'input--with-prefix' : '';
  const suffixClass = suffix ? 'input--with-suffix' : '';
  
  const inputClass = [
    baseClass,
    sizeClass,
    errorClass,
    disabledClass,
    prefixClass,
    suffixClass,
    className
  ].filter(Boolean).join(' ');

  const displayError = error && errorText;
  const displayHelper = !displayError && helperText;

  return (
    <div className="input-wrapper">
      {label && (
        <label className="input-label">
          {label}
        </label>
      )}
      
      <div className="input-container">
        {prefix && (
          <div className="input-prefix">
            {prefix}
          </div>
        )}
        
        <input
          ref={ref}
          type={type}
          className={inputClass}
          disabled={disabled}
          placeholder={placeholder}
          {...props}
        />
        
        {suffix && (
          <div className="input-suffix">
            {suffix}
          </div>
        )}
      </div>
      
      {(displayError || displayHelper) && (
        <div className={`input-message ${displayError ? 'input-message--error' : 'input-message--helper'}`}>
          {displayError ? errorText : helperText}
        </div>
      )}
    </div>
  );
});

Input.displayName = 'Input';

export default Input;
